import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import { motion } from 'framer-motion'

const LAST_UPDATED = 'February 2026'

export default function PrivacyPolicyPage() {
  return (
    <main data-testid="privacy-policy-page">
      <Navbar />
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24 bg-white min-h-screen">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-6 font-body font-semibold">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter text-[#0A0A0A] mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-sm font-body text-[#0A0A0A]/40 mb-16">
              Last updated: {LAST_UPDATED}
            </p>

            <div className="prose-custom space-y-12">
              <Section title="1. Who We Are">
                <p>
                  Strive Partners ("we", "us", "our") is a specialist tech and GCC executive search firm based in Bengaluru, India. We operate the website at strivepartners.co and related subdomains. For questions about this policy, contact us at{' '}
                  <a href="mailto:sahil@strivepartners.co" className="text-[#0B1F3A] underline underline-offset-2">
                    sahil@strivepartners.co
                  </a>.
                </p>
              </Section>

              <Section title="2. Information We Collect">
                <p>We collect information you provide when you:</p>
                <ul>
                  <li>Submit an enquiry through our contact form (name, work email, company, role, location, message)</li>
                  <li>Send us an email directly</li>
                  <li>Engage with us during a search process</li>
                </ul>
                <p className="mt-4">
                  We also collect standard web analytics data (page views, device type, referrer) via PostHog to understand how our site is used. This data is anonymised at the analytics level and not linked to individuals without consent.
                </p>
              </Section>

              <Section title="3. How We Use Your Information">
                <p>We use collected information to:</p>
                <ul>
                  <li>Respond to your enquiries and discuss potential search mandates</li>
                  <li>Communicate about ongoing or potential engagements</li>
                  <li>Improve our website and services based on aggregate usage data</li>
                  <li>Meet our legal and compliance obligations</li>
                </ul>
                <p className="mt-4">
                  We do not use your information for unsolicited marketing. We do not sell, trade, or rent your personal information to third parties.
                </p>
              </Section>

              <Section title="4. Legal Basis for Processing (GDPR)">
                <p>
                  Where applicable, we process your data under the following lawful bases: (a) your consent when submitting an enquiry form; (b) legitimate interests in responding to business communications; and (c) compliance with legal obligations.
                </p>
              </Section>

              <Section title="5. Data Storage and Security">
                <p>
                  Enquiry data is stored securely in a cloud-hosted MongoDB database. Emails sent to sahil@strivepartners.co are processed via Resend, a transactional email provider. We implement appropriate technical and organisational measures to protect your data against unauthorised access, disclosure, or loss.
                </p>
                <p className="mt-4">
                  We retain enquiry data for up to 24 months from the date of submission, after which it is deleted unless an ongoing engagement requires longer retention.
                </p>
              </Section>

              <Section title="6. Third-Party Services">
                <p>Our website uses the following third-party services, each governed by their own privacy policies:</p>
                <ul>
                  <li><strong>PostHog</strong> — product analytics (anonymised)</li>
                  <li><strong>Resend</strong> — transactional email delivery</li>
                  <li><strong>Google Fonts</strong> — typeface delivery (Manrope, Inter)</li>
                  <li><strong>MongoDB Atlas</strong> — cloud database storage</li>
                </ul>
              </Section>

              <Section title="7. Your Rights">
                <p>You have the right to:</p>
                <ul>
                  <li>Request access to personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing or request restriction</li>
                  <li>Withdraw consent at any time (where processing is based on consent)</li>
                </ul>
                <p className="mt-4">
                  To exercise any of these rights, email{' '}
                  <a href="mailto:sahil@strivepartners.co" className="text-[#0B1F3A] underline underline-offset-2">
                    sahil@strivepartners.co
                  </a>. We will respond within 30 days.
                </p>
              </Section>

              <Section title="8. Cookies">
                <p>
                  We use essential session cookies and PostHog analytics cookies. No advertising or tracking cookies are used. You can disable cookies in your browser settings; doing so will not affect your ability to use the site.
                </p>
              </Section>

              <Section title="9. Changes to This Policy">
                <p>
                  We may update this policy from time to time. The "last updated" date at the top reflects the most recent revision. Continued use of the site after changes constitutes acceptance of the updated policy.
                </p>
              </Section>

              <Section title="10. Governing Law">
                <p>
                  This Privacy Policy is governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.
                </p>
              </Section>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-heading font-bold text-[#0A0A0A] mb-4 tracking-tight">{title}</h2>
      <div className="text-sm font-body text-[#0A0A0A]/60 leading-relaxed space-y-3 [&_ul]:list-none [&_ul]:space-y-2 [&_ul_li]:flex [&_ul_li]:items-start [&_ul_li]:gap-2 [&_ul_li]:before:content-['—'] [&_ul_li]:before:text-[#0A0A0A]/25 [&_ul_li]:before:flex-shrink-0 [&_strong]:text-[#0A0A0A]/80 [&_strong]:font-semibold">
        {children}
      </div>
    </div>
  )
}
